import { Button, DatePicker, Form, Input, Select, Typography, Divider } from "antd";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const AddPIP = ({ form, onSubmit }) => {
	const dispatch = useDispatch();
	const [loader, setLoader] = React.useState(false);
	const staffList = useSelector((state) => state.users?.list || []);

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const onFinish = async (values) => {
		try {
			setLoader(true);
			const pipData = {
				...values,
				startDate: dayjs(values.startDate).format("YYYY-MM-DD"),
				endDate: values.endDate ? dayjs(values.endDate).format("YYYY-MM-DD") : null,
				reviewDate: values.reviewDate ? dayjs(values.reviewDate).format("YYYY-MM-DD") : null,
			};

			if (onSubmit) {
				onSubmit(pipData);
				toast.success("Performance Improvement Plan Created Successfully!");
			}
		} catch (error) {
			toast.error("Failed to create PIP");
		} finally {
			setLoader(false);
		}
	};

	const onFinishFailed = (errorInfo) => {
		toast.warning("Please fill all required fields");
		setLoader(false);
	};

	return (
		<div className="column-design border rounded card-custom">
			<Title level={4} className="m-2 mt-5 mb-5 text-center">
				Performance Improvement Plan
			</Title>
			<Form
				form={form}
				style={{ marginBottom: "40px" }}
				name="pip-form"
				labelCol={{ span: 8 }}
				wrapperCol={{ span: 16 }}
				onFinish={onFinish}
				onFinishFailed={onFinishFailed}
				autoComplete="off"
				preserve={false}
			>
				<Form.Item
					label={<span className="required-label">Employee</span>}
					name="employeeId"
					rules={[
						{ required: true, message: "Please select an employee!" },
					]}
				>
					<Select
						placeholder="Select employee"
						showSearch
						filterOption={(input, option) =>
							(option?.children ?? "")
								.toLowerCase()
								.includes(input.toLowerCase())
						}
					>
						{staffList?.map((staff) => (
							<Option key={staff.id} value={staff.id}>
								{staff.EmployeeName ||
									`${staff.firstName || ""} ${staff.lastName || ""}`.trim() ||
									staff.UserName}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Manager</span>}
					name="managerId"
					rules={[
						{ required: true, message: "Please select a manager!" },
					]}
				>
					<Select
						placeholder="Select manager"
						showSearch
						filterOption={(input, option) =>
							(option?.children ?? "")
								.toLowerCase()
								.includes(input.toLowerCase())
						}
					>
						{staffList?.map((staff) => (
							<Option key={staff.id} value={staff.id}>
								{staff.EmployeeName ||
									`${staff.firstName || ""} ${staff.lastName || ""}`.trim() ||
									staff.UserName}
							</Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">PIP Start Date</span>}
					name="startDate"
					rules={[
						{ required: true, message: "Please select start date!" },
					]}
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
					/>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">PIP End Date</span>}
					name="endDate"
					rules={[
						{ required: true, message: "Please select end date!" },
					]}
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
					/>
				</Form.Item>

				<Form.Item
					label="Next Review Date"
					name="reviewDate"
				>
					<DatePicker
						style={{ width: "100%" }}
						format="DD-MM-YYYY"
					/>
				</Form.Item>

				<Divider orientation="left">Performance Issues</Divider>

				<Form.Item
					label={<span className="required-label">Performance Concerns</span>}
					name="performanceConcerns"
					rules={[
						{ required: true, message: "Please describe performance concerns!" },
					]}
				>
					<TextArea
						rows={3}
						placeholder="Describe the specific performance issues and concerns"
						maxLength={500}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Root Cause Analysis</span>}
					name="rootCause"
					rules={[
						{ required: true, message: "Please provide root cause analysis!" },
					]}
				>
					<TextArea
						rows={2}
						placeholder="Identify the root causes of the performance issues"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Divider orientation="left">Improvement Goals</Divider>

				<Form.Item
					label={<span className="required-label">Improvement Objectives</span>}
					name="improvementObjectives"
					rules={[
						{ required: true, message: "Please define improvement objectives!" },
					]}
				>
					<TextArea
						rows={3}
						placeholder="Define clear, measurable improvement objectives"
						maxLength={500}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Success Criteria</span>}
					name="successCriteria"
					rules={[
						{ required: true, message: "Please define success criteria!" },
					]}
				>
					<TextArea
						rows={2}
						placeholder="Define measurable success criteria for the PIP"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Divider orientation="left">Action Plan</Divider>

				<Form.Item
					label={<span className="required-label">Action Items</span>}
					name="actionItems"
					rules={[
						{ required: true, message: "Please list action items!" },
					]}
				>
					<TextArea
						rows={3}
						placeholder="List specific action items and steps to be taken"
						maxLength={500}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label="Support & Resources"
					name="supportResources"
				>
					<TextArea
						rows={2}
						placeholder="Describe support, training, or resources to be provided"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label="Milestones"
					name="milestones"
				>
					<TextArea
						rows={2}
						placeholder="Define key milestones and checkpoints"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Divider orientation="left">Additional Information</Divider>

				<Form.Item
					label="Manager Comments"
					name="managerComments"
				>
					<TextArea
						rows={2}
						placeholder="Additional comments from manager"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label="Employee Comments"
					name="employeeComments"
				>
					<TextArea
						rows={2}
						placeholder="Employee's acknowledgment and comments"
						maxLength={300}
						showCount
					/>
				</Form.Item>

				<Form.Item
					label="Status"
					name="status"
					initialValue="Draft"
				>
					<Select>
						<Option value="Draft">Draft</Option>
						<Option value="Active">Active</Option>
						<Option value="Under Review">Under Review</Option>
						<Option value="Completed">Completed</Option>
						<Option value="Terminated">Terminated</Option>
					</Select>
				</Form.Item>

				<Form.Item wrapperCol={{ offset: 8, span: 16 }}>
					<Button
						type="primary"
						size="large"
						htmlType="submit"
						block
						loading={loader}
					>
						Create PIP
					</Button>
				</Form.Item>
			</Form>
		</div>
	);
};

export default AddPIP;

